import React, {useState, useCallback } from 'react';
import { Box, Grid, Divider, Link, Button, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText, Menu, MenuItem, IconButton, Select, FormControl } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useNavigate } from 'react-router-dom';
import { datadogRum  } from '@datadog/browser-rum';
import { useOutletContext } from 'react-router-dom';

import IdentifiedSpeciesVersionDialog from '../Runs/IdentifiedSpeciesVersionDialog'

import { removePrefixFromAnalysisVersion } from '../Analyses/canonicalizeMetadata';
import { useDatasets, useProjects } from '../services/api/KeynomeAPIHooks';
import { useMoveSampleToDataset, useUpdateSample } from '../services/api/KeynomeAPIMutations';
import { filterSupportedSampleTypes } from './utils/sampleTypes';

function DetailedSampleMetadataSummary(props) {
  const navigate = useNavigate();
  const {
    sample,
    analysis,
    activeProject,
    isIllumina,
  } = props;
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const open = Boolean(menuAnchorEl);
  const [versionDialogOpen, setVersionDialogOpen] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const handleArchiveClick = () => {
    setArchiveDialogOpen(true);
  };

const { mutateAsync: moveLibraryToDataset, isPending: isArchiving } = useMoveSampleToDataset();

const { data: projects } = useProjects(activeProject?.attributes?.organization_id);
const archiveProject = (projects || []).find(project => project.attributes.name === "archive");

const { data: datasets } = useDatasets(archiveProject?.id);
const archiveDataset = (datasets || []).find(dataset => dataset.attributes.name === "archive");


const handleMenuClick = (event) => {
  setMenuAnchorEl(event.currentTarget);
};

const handleMenuClose = () => {
  setMenuAnchorEl(null);
};

const handleArchiveConfirm = useCallback(() => {
  const archiveLibrary = async () => {
     try {
       await moveLibraryToDataset({ sampleId: sample.id, destDatasetId: archiveDataset.id });
       setArchiveDialogOpen(false);
       navigate('/');
     } catch (error) {
        datadogRum.addError(error);
        alert(`Failed to archive sample: ${error.message}`);
      }
    }

 archiveLibrary();
}, [archiveDataset, sample, moveLibraryToDataset, navigate]);

  const handleArchiveCancel = () => {
    setArchiveDialogOpen(false);
  };
  let analysisExecutionTimeString = null;
    if (analysis) {
        const diffMs = new Date(analysis.attributes.updated_at) - new Date(analysis.attributes.created_at)
        const diffMins = Math.round(diffMs / 60000);
        analysisExecutionTimeString = `(${diffMins} min.)`
    }
  return (
    <>
      <Box sx={{ backgroundColor: '#eee', padding: '1px 10px' }}>
        <Grid container>
          <Grid item xs={6}>
            <p><b>SAMPLE / ANALYSIS</b></p>
          </Grid>
          {archiveDataset ? (
            <Grid item xs={6} sx={{textAlign: 'right', paddingTop: "5px"}}>
              <IconButton
                onClick={handleMenuClick}
              >
                <MoreVertIcon />
              </IconButton>
              <Menu
                anchorEl={menuAnchorEl}
                open={open}
                onClose={handleMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <MenuItem onClick={() => {
                  handleArchiveClick();
                  handleMenuClose();
                }}>
                  Archive Sample
                </MenuItem>
              </Menu>
            </Grid>
          ): null}
          </Grid>
      </Box>
      <Dialog
        open={archiveDialogOpen}
        onClose={handleArchiveCancel}
      >
        <DialogTitle>Archive Sample</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to archive this sample? After archiving, you will no longer be able to see any results associated with this sample.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleArchiveCancel} disabled={isArchiving}>Cancel</Button>
          <Button 
            onClick={handleArchiveConfirm}
            color="primary"
            variant="contained"
            autoFocus
            disabled={isArchiving}
          >
            {isArchiving ? 'Archiving...' : 'Yes, Archive'}
          </Button>
        </DialogActions>
      </Dialog>
      <Box sx={{ padding: '1px 10px' }}>
        <p>
          <b>Sample Name:</b>
          {' '}
          {sample?.attributes.sample_name || sample?.attributes.sample_id || sample?.attributes.library_id || 'N/A'}
        </p>
        <SampleTypeSummary sample={sample} isIllumina={isIllumina} />
        <div>
            <Divider />
            &nbsp;
        </div>
        <p><b>Analysis Version</b>: {analysis?.attributes.metadata.minion_pipeline_version ? (<Link onClick={() => setVersionDialogOpen(true)}sx={{'&:hover': {color: 'orange', cursor: 'pointer', textDecoration: 'underline'}}}>{removePrefixFromAnalysisVersion(analysis.attributes.metadata.minion_pipeline_version)}</Link>) : '-'}</p>
        <p><b>Analysis Started At</b>: {analysis ? new Date(analysis.attributes.created_at).toLocaleString() : null}</p>
        <p><b>Analysis Completed At</b>: {analysis ? new Date(analysis.attributes.updated_at).toLocaleString() : null} <span style={{color: 'gray'}}>{analysisExecutionTimeString}</span></p>
      </Box>
      <IdentifiedSpeciesVersionDialog open={versionDialogOpen} setOpen={setVersionDialogOpen} analysis={analysis} />
    </>
  );
}

function SampleTypeSummary({ sample, isIllumina }) {
  const { setUserErrorMessage, supportedSampleTypes: allSampleTypes } = useOutletContext();
  const supportedSampleTypes = filterSupportedSampleTypes(allSampleTypes, isIllumina);

  const { mutateAsync: updateSample, isPending: updatingSample } = useUpdateSample();

  const handleUpdateSample = async (newSampleType) => {
    try {
      await updateSample({ sampleId: sample.id, sampleType: newSampleType });
    } catch (error) {
      setUserErrorMessage('Failed to update sample type. Please wait a few minutes and try again. Contact support@dayzerodiagnostics.com if you require assistance.');
    }
  };

return <Box display="flex" alignItems="center" gap={1} marginBottom={1.75}>
  <b>Sample Type:</b>
  {
    !!!sample || (supportedSampleTypes?.length || 0) <= 1
      ? (
        sample?.attributes.sample_type || 'Unknown'
      )
      : (
        <FormControl variant="standard">
          <Select
            value={sample.attributes.sample_type}
            onChange={(event) => handleUpdateSample(event.target.value)}
            sx={{height: 20}}
            disabled={updatingSample}
            disableUnderline
          >
            {supportedSampleTypes.map((sampleType) => (
              <MenuItem key={sampleType} value={sampleType}>
                {sampleType}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )
  }
</Box>

}

export default DetailedSampleMetadataSummary;
